import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { GoogleButton } from "@/components/GoogleAuth";
import { useAuth } from "@/context/AuthContext";
import { errMsg, HORSE_2 } from "@/lib/api";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const next = params.get("next");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const u = isLogin ? await login(form.email, form.password) : await register(form.name, form.email, form.password);
      toast.success(isLogin ? `Hola de nuevo, ${u.name}` : "Cuenta creada. ¡Bienvenido a FAUNNY!");
      nav(next || (u.role === "admin" ? "/admin" : "/cuenta"));
    } catch (e2) {
      setError(errMsg(e2));
    }
    setBusy(false);
  };
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="grid lg:grid-cols-2 min-h-[calc(100vh-100px)]" data-testid={`${mode}-page`}>
      <div className="hidden lg:block relative">
        <img src={HORSE_2} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0B1D3A]/70" />
        <div className="relative p-14 text-white h-full flex flex-col justify-end">
          <h2 className="font-display font-black text-6xl leading-none">Exigencia, dedicación y el mejor cuidado</h2>
          <p className="text-blue-100 mt-4 max-w-md">Guarda tus pedidos, tu dirección y las dietas de tus caballos.</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-4 py-16">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5">
          <h1 className="font-display font-black text-4xl sm:text-5xl text-[#0B1D3A]">{isLogin ? "Iniciar sesión" : "Crear cuenta"}</h1>
          {next === "/checkout" && <p className="text-sm bg-[#EFF6FF] text-[#0B1D3A] p-3 rounded" data-testid="auth-checkout-notice">Inicia sesión o crea una cuenta para finalizar tu compra.</p>}
          {!isLogin && (
            <div><Label htmlFor="name" className="text-base font-bold">Nombre</Label><Input id="name" required value={form.name} onChange={upd("name")} data-testid="auth-name-input" className="mt-1.5 h-12 text-lg md:text-lg" /></div>
          )}
          <div><Label htmlFor="email" className="text-base font-bold">Email</Label><Input id="email" type="email" autoComplete="email" required value={form.email} onChange={upd("email")} data-testid="auth-email-input" className="mt-1.5 h-12 text-lg md:text-lg" /></div>
          <div><Label htmlFor="password" className="text-base font-bold">Contraseña</Label><PasswordInput id="password" required minLength={6} value={form.password} onChange={upd("password")} testId="auth-password-input" autoComplete={isLogin ? "current-password" : "new-password"} className="h-12 rounded-md px-3 text-lg" /></div>
          {error && <p className="text-sm text-[#DC2626] font-semibold" data-testid="auth-error">{error}</p>}
          {isLogin && <Link to="/recuperar" className="block text-sm font-bold text-[#2563EB] hover:underline" data-testid="auth-forgot-link">¿Has olvidado tu contraseña?</Link>}
          {!isLogin && (
            <label className="flex items-start gap-2.5 text-sm cursor-pointer">
              <input type="checkbox" required className="mt-0.5 accent-[#DC2626] w-4 h-4" data-testid="auth-accept-privacy"
                onInvalid={(e) => e.target.setCustomValidity("Debes aceptar la política de privacidad")} onChange={(e) => e.target.setCustomValidity("")} />
              <span>Acepto la <Link to="/legal/privacidad" target="_blank" className="text-[#2563EB] font-bold underline">política de privacidad</Link>.</span>
            </label>
          )}
          <button disabled={busy} data-testid="auth-submit-button" className="w-full bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-60 text-white font-display font-bold text-lg py-3.5 rounded-full transition-colors">
            {busy ? "Un momento…" : isLogin ? "Entrar" : "Crear cuenta"}
          </button>
          <div className="flex items-center gap-3 text-sm text-slate-400"><span className="flex-1 h-px bg-slate-200" />o<span className="flex-1 h-px bg-slate-200" /></div>
          <GoogleButton next={next} />
          <p className="text-sm text-center text-slate-600">
            {isLogin ? "¿No tienes cuenta? " : "¿Ya tienes cuenta? "}
            <Link to={`${isLogin ? "/registro" : "/login"}${next ? `?next=${next}` : ""}`} className="font-bold text-[#2563EB] hover:text-[#DC2626]" data-testid="auth-switch-link">
              {isLogin ? "Regístrate" : "Inicia sesión"}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
